import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormField } from 'ui/formField';
import { HelpToggletip } from 'ui/tooltip';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import {
  StyledRadioPercentSign,
  StyledOptions,
  StyledSecondaryOptionLabel,
  StyledCheckboxWrapper,
  StyledHoldoutGrid,
  StyledSmallerInputText,
  StyledHoldoutSectionText,
  StyledHoldoutSizeGridSection,
  StyledHoldoutTextGridSection,
  StyledAttributionTimeGridSection,
  StyledFields,
  StyledErrorMessage,
  StyledInfoTooltip,
  AddHoldoutGroupLabel,
} from 'modules/experiments/components/pages/edit/sections/settings/styled';
import { UpdateExperimentSettingsFormFieldNames } from 'modules/experiments/constants/updateExperimentSettings';
import { CheckboxField } from 'lib/iterable-hook-form';
import { getTakeoverCampaignConversions } from 'modules/experiments/ducks/selectors';
import {
  DEFAULT_HOLDOUT_GROUP_SIZE,
  DEFAULT_ATTRIBUTION_PERIOD_HOURS,
  MIN_HOLDOUT_GROUP_SIZE,
  MAX_HOLDOUT_GROUP_SIZE,
  MIN_ATTRIBUTION_PERIOD_HOURS,
  MAX_ATTRIBUTION_PERIOD_HOURS,
} from 'modules/experiments/takeover/constants';
import { baseColors } from 'ui/theme';

export const AudienceFormHoldoutSetting = () => {
  const { t } = useExperimentsTranslations();

  const {
    formState: { errors },
    trigger,
  } = useFormContext();

  const {
    HOLDOUT_GROUP_SIZE,
    ATTRIBUTION_PERIOD_HOURS,
    HAS_HOLDOUT_GROUP,
    HOLDOUT_TEST_SIZE_PERCENTAGE,
  } = UpdateExperimentSettingsFormFieldNames;

  const [watchHasHoldOutGroup, watchTestSizePercentage] = useWatch({
    name: [HAS_HOLDOUT_GROUP, HOLDOUT_TEST_SIZE_PERCENTAGE],
  });

  useEffect(() => {
    trigger();
  }, [watchHasHoldOutGroup, watchTestSizePercentage]);

  const campaignConversions = useSelector(getTakeoverCampaignConversions);
  const containsConversions = campaignConversions.length > 0;

  return (
    <StyledHoldoutGrid data-test="audience-form-holdout-setting">
      <StyledHoldoutSectionText>{t('settings.settingsLabels.holdout')}</StyledHoldoutSectionText>
      <StyledHoldoutTextGridSection>
        <StyledCheckboxWrapper data-test="add-holdout-group-checkbox">
          <StyledInfoTooltip
            isEnabled={!containsConversions}
            tooltipContent={t('settings.requireCustomEventInCampaign')}
          >
            <CheckboxField isDisabled={!containsConversions} name={HAS_HOLDOUT_GROUP} />
          </StyledInfoTooltip>
        </StyledCheckboxWrapper>
        <StyledOptions>
          <AddHoldoutGroupLabel isDisabled={!containsConversions}>
            {t('settings.holdOutGroupSetting')}
          </AddHoldoutGroupLabel>
          <HelpToggletip
            iconProps={{ color: baseColors.grey50 }}
            tooltipContent={t('settings.holdOutGroupTooltip')}
          />
        </StyledOptions>
      </StyledHoldoutTextGridSection>
      {watchHasHoldOutGroup && (
        <>
          <StyledHoldoutSizeGridSection data-test="holdout-group-size-wrap">
            <StyledFields>
              <StyledSecondaryOptionLabel data-test="holdout-label">
                {t('settings.holdOutGroupSize.label')}
              </StyledSecondaryOptionLabel>
              <FormField isHorizontalLayout inputId="holdout-group-size">
                <StyledSmallerInputText
                  name={HOLDOUT_GROUP_SIZE}
                  valueAsNumber={true}
                  minValue={MIN_HOLDOUT_GROUP_SIZE}
                  maxValue={MAX_HOLDOUT_GROUP_SIZE}
                  data-test={'holdout-group-size"'}
                  type="number"
                  id="holdout-group-size"
                  Suffix={<StyledRadioPercentSign>%</StyledRadioPercentSign>}
                  placeholder={`${DEFAULT_HOLDOUT_GROUP_SIZE}`}
                />
              </FormField>
            </StyledFields>
            {!!errors[HOLDOUT_GROUP_SIZE] && (
              <StyledErrorMessage>
                {/* @ts-expect-error */}
                {errors[HOLDOUT_GROUP_SIZE]?.message}
              </StyledErrorMessage>
            )}
          </StyledHoldoutSizeGridSection>

          <StyledAttributionTimeGridSection data-test="holdout-attribution-window-wrap">
            <StyledFields>
              <StyledSecondaryOptionLabel>
                {t('settings.attributionPeriodHours.label')}
              </StyledSecondaryOptionLabel>
              <FormField
                hasError={!!errors[ATTRIBUTION_PERIOD_HOURS]}
                isHorizontalLayout
                inputId="attributionPeriodHours"
              >
                <StyledSmallerInputText
                  name={ATTRIBUTION_PERIOD_HOURS}
                  valueAsNumber={true}
                  minValue={MIN_ATTRIBUTION_PERIOD_HOURS}
                  maxValue={MAX_ATTRIBUTION_PERIOD_HOURS}
                  type="number"
                  Suffix={<>{t('settings.hours')}</>}
                  placeholder={`${DEFAULT_ATTRIBUTION_PERIOD_HOURS}`}
                />
              </FormField>
            </StyledFields>
            {!!errors[ATTRIBUTION_PERIOD_HOURS] && (
              <StyledErrorMessage>
                {/* @ts-expect-error */}
                {errors[ATTRIBUTION_PERIOD_HOURS]?.message}
              </StyledErrorMessage>
            )}
          </StyledAttributionTimeGridSection>
        </>
      )}
    </StyledHoldoutGrid>
  );
};
